import { request, unwrap } from '../client';
import type {
  ApiResponse,
  DeployRoutingResponse,
  RoutingReadiness,
  TenantRoutingDeployResponse,
} from '../types';

/**
 * Routing readiness + deploy (spec 0042; companion to saas-auth 0046).
 *
 * Both endpoints are feature-detected: they are absent until saas-auth 0046
 * ships. `routingReadiness` resolves to null on a 404 (or any not-yet-available
 * error) so the "Deploy routing changes" button stays inert — never enabled
 * without a positive `ready`. The POST is only reachable once the button
 * enables, so a deploy never fires against a missing endpoint.
 *
 * NOTE (reconcile at saas-auth 0046 ship): built to the documented contract and
 * the dominant `/api/saas-admin/**` envelope pattern (`{ success, message, data }`
 * → unwrap). If 0046 returns these payloads flat, drop the unwrap here.
 */
export const routingApi = {
  async routingReadiness(tenantId: number): Promise<RoutingReadiness | null> {
    try {
      const res = await request<ApiResponse<RoutingReadiness>>(
        'GET',
        `/api/saas-admin/tenants/${tenantId}/routing-readiness`,
      );
      return unwrap(res);
    } catch {
      // Pre-0046 the endpoint 404s; treat any failure as "not available yet".
      return null;
    }
  },

  // Spec 0064 (saas-auth SASATH-0093) — platform-wide, synchronous & in-app:
  // renders + validates + reloads Caddy in one request and returns result counts
  // (no GitHub run). No body. 500 CADDY_CONFIG_INVALID / CADDY_RELOAD_FAILED
  // surface via ApiError. `forceServiceRestart` (deprecated) is never sent.
  async deployRouting(): Promise<DeployRoutingResponse> {
    const res = await request<ApiResponse<DeployRoutingResponse>>(
      'POST',
      '/api/saas-admin/routing/deploy',
    );
    return unwrap(res);
  },

  // Spec 0064 (saas-auth SASATH-0093) — per-tenant in-app render + Caddy reload.
  // No body. 404 TENANT_NOT_FOUND / 500 CADDY_* surface via ApiError.
  async deployTenantRouting(tenantId: number): Promise<TenantRoutingDeployResponse> {
    const res = await request<ApiResponse<TenantRoutingDeployResponse>>(
      'POST',
      `/api/saas-admin/tenants/${tenantId}/routing/deploy`,
    );
    return unwrap(res);
  },
};
